import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

import LoadingSpinner from '../components/LoadingSpinner';

// --- Styled Components ---

const PageContainer = styled.div`
  padding: 40px 20px;
  max-width: 900px;
  margin: 40px auto;
  background-color: ${props => props.theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin: 12px;
    padding: 20px 14px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  color: ${props => props.theme.colors.primaryBlue};
  text-align: center;
  margin-bottom: 30px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const ContractList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContractCard = styled.div`
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.theme.colors.secondaryBlue};
  }

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.primaryBlue};
      background-color: #f0f8ff;
      box-shadow: 0 4px 15px rgba(30, 144, 255, 0.2);

      &::after {
        content: 'Selecionado';
        position: absolute;
        top: 15px;
        right: -40px;
        background: ${theme.colors.primaryBlue};
        color: white;
        padding: 5px 40px;
        font-size: 12px;
        font-weight: 600;
        transform: rotate(45deg);
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
      }
    `}
  
  ${({ isInactive }) =>
    isInactive &&
    css`
      opacity: 0.6;
      background: #f1f1f1;
      cursor: not-allowed;

      &:hover {
        transform: none;
        box-shadow: none;
        border-color: #e5e7eb;
      }
    `}
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const PlanName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: white;
  background-color: ${({ status, theme }) =>
    status === 'Ativo' ? theme.colors.success : theme.colors.danger};
`;

const CardBody = styled.div`
  font-size: 16px;
  color: #555;
  line-height: 1.6;

  strong {
    color: #333;
  }
`;

const Contrato = () => {
  const navigate = useNavigate();
  const { user, contratos, selectedContrato, selectContrato, loading } = useAuth();

  const handleSelectContract = (contract) => {
    if (contract.status !== 'ATIVO') {
      toast.warn('Este contrato não está ativo e não pode ser selecionado.');
      return;
    }
    // Chama a função do contexto para atualizar o contrato selecionado globalmente
    selectContrato(contract);
    // Redireciona o usuário para o dashboard para ver os dados do novo contrato
    navigate('/dashboard');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageContainer>
      <Title>Meus Contratos</Title>
      {contratos && contratos.length > 0 ? (
        <ContractList>
          {contratos.map((contract) => (
            <ContractCard
              key={contract.numero}
              isSelected={selectedContrato?.numero === contract.numero}
              isInactive={contract.status !== 'ATIVO'}
              onClick={() => handleSelectContract(contract)}
              role="button"
              aria-pressed={selectedContrato?.numero === contract.numero}
              tabIndex={contract.status === 'ATIVO' ? 0 : -1}
            >
              <CardHeader>
                <PlanName>{contract.plano || 'Plano não informado'}</PlanName>
              </CardHeader>
              <CardBody>
                <div>Número do Contrato: <strong>{contract.numero}</strong></div>
                <div>Titular: <strong>{user?.nome}</strong></div>
              </CardBody>
            </ContractCard>
          ))}
        </ContractList>
      ) : (
        <p style={{ textAlign: 'center', color: '#555' }}>
          Nenhum contrato encontrado para este usuário.
        </p>
      )}
      </PageContainer>
    </motion.div>
  );
};

export default Contrato;